"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createWebsiteTheme = void 0;
const material_1 = require("@mui/material");
const styles_1 = require("@mui/material/styles");
const colors = require("@ansarada/colors");
const fonts_1 = require("../fonts");
function createPalette(mode) {
    return {
        primary: {
            main: colors.projectCitrus.primary[500],
        },
        secondary: {
            main: colors.projectCitrus.common.black,
        },
        error: {
            main: colors.functionalRed600,
            contrastText: colors.white,
        },
        warning: {
            main: colors.citrus,
            light: colors.mandarine,
            contrastText: colors.textBase,
        },
        info: {
            main: colors.dawn,
            light: colors.berry,
            contrastText: colors.textBase,
        },
        success: {
            main: colors.functionalGreen500,
            contrastText: colors.white,
        },
        mode,
        common: {
            black: colors.projectCitrus.common.black,
            white: colors.white,
        },
        text: {
            primary: mode === 'light' ? colors.textBase : colors.textWhite,
            secondary: mode === 'light' ? colors.textLight : colors.textWhite,
        },
        grey: {
            100: colors.neutral100,
            200: colors.neutral200,
            300: colors.neutral300,
            400: colors.neutral400,
            500: colors.neutral500,
            600: colors.neutral600,
        },
        divider: colors.neutral300,
    };
}
function createWebsiteTheme({ name, mode }) {
    const themeOptions = {
        name,
        palette: createPalette(mode),
        spacing: [2, 4, 8, 12, 16, 24, 32, 64, 80, 128, 140],
        shape: {
            borderRadius: 0,
        },
        shadows: [
            'none',
            '0px 1px 2px rgba(0, 0, 0, 0.2), 0px 1px 3px rgba(0, 0, 0, 0.1)',
            '0px 3px 6px rgba(0, 0, 0, 0.23), 0px 3px 6px rgba(0, 0, 0, 0.16)',
            '0px 6px 6px rgba(0, 0, 0, 0.26), 0px 10px 20px rgba(0, 0, 0, 0.19)',
            '0px 10px 10px rgba(0, 0, 0, 0.26), 0px 14px 28px rgba(0, 0, 0, 0.25)',
            '0px 15px 12px rgba(0, 0, 0, 0.22), 0px 19px 38px rgba(0, 0, 0, 0.3)',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
        ],
        components: {
            MuiCssBaseline: {
                styleOverrides: `
          ${(0, fonts_1.generateAnsaradaFavoritFontFace)('variable')}
          ${(0, fonts_1.generateAnsaradaFavoritFontFace)('regular')}
          ${(0, fonts_1.generateAnsaradaFavoritFontFace)('bold')}

          @supports (font-variation-settings: normal) {
            * {
              font-family: ${fonts_1.ANSARADA_VARIABLE_FONTS.join(',')} !important;
            }
          }
        `,
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        [`&.${material_1.formLabelClasses.focused}`]: {
                            color: `${colors.projectCitrus.common.black}`,
                        },
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        [`&.${material_1.accordionSummaryClasses.root}`]: {
                            boxShadow: 'none',
                        },
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        '&.MuiPaper': {
                            boxShadow: 'none',
                        },
                        boxShadow: 'none',
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused > fieldset.MuiOutlinedInput-notchedOutline': {
                            borderColor: `${colors.projectCitrus.common.black}`,
                        },
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: ({ ownerState }) => (Object.assign(Object.assign({ color: colors.projectCitrus.common.black, backgroundColor: colors.projectCitrus.primary[500] }, (ownerState.underline === 'hover' && {
                        '&:hover': {
                            textDecoration: 'underline !important',
                        },
                    })), (ownerState.underline === 'always' && {
                        textDecoration: 'underline !important',
                    }))),
                },
                variants: [
                    {
                        props: { variant: 'button' },
                        style: {
                            backgroundColor: 'transparent',
                            textDecorationColor: colors.projectCitrus.common.black,
                            ':hover': {
                                backgroundColor: colors.projectCitrus.primary[500],
                            },
                        },
                    },
                ],
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '200px',
                    },
                    outlined: {
                        color: colors.projectCitrus.common.black,
                        borderColor: colors.projectCitrus.common.black,
                        '&:hover': {
                            borderColor: colors.projectCitrus.common.black,
                            opacity: 0.8,
                        },
                    },
                    contained: {
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                    },
                },
            },
            MuiStepIcon: {
                styleOverrides: {
                    root: {
                        color: colors.projectCitrus.common.white,
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        color: colors.projectCitrus.common.black,
                        '&.Mui-checked': {
                            color: colors.projectCitrus.common.black,
                        },
                    },
                },
            },
        },
        typography: {
            fontFamily: fonts_1.ANSARADA_REGULAR_FONTS.join(','),
            h1: Object.assign(Object.assign({ fontSize: '92px', lineHeight: '92px', letterSpacing: '-3.07px', fontWeight: 700 }, (0, fonts_1.supportVariableFont)('bold', { fontVariationSettings: "'wdth' 700" })), { '@media (max-width:600px)': {
                    fontSize: '56px',
                    lineHeight: '60px',
                } }),
            h2: Object.assign(Object.assign({ fontSize: '72px', lineHeight: '76px', letterSpacing: '-3.09px', fontWeight: 700 }, (0, fonts_1.supportVariableFont)('bold', { fontVariationSettings: "'wdth' 700" })), { '@media (max-width:600px)': {
                    fontSize: '56px',
                    lineHeight: '60px',
                    letterSpacing: '-3.07px',
                } }),
            h3: Object.assign(Object.assign({ fontSize: '52px', lineHeight: '56px', letterSpacing: '-2.08px', fontWeight: 700 }, (0, fonts_1.supportVariableFont)('bold', { fontVariationSettings: "'wdth' 700" })), { '@media (max-width:600px)': {
                    fontSize: '36px',
                    lineHeight: '40px',
                    letterSpacing: '-0.21px',
                } }),
            h4: Object.assign(Object.assign({ fontSize: '52px', lineHeight: '56px', letterSpacing: '-2.08px', fontWeight: 700 }, (0, fonts_1.supportVariableFont)('bold', { fontVariationSettings: "'wdth' 700" })), { '@media (max-width:600px)': {
                    fontSize: '36px',
                    lineHeight: '40px',
                    letterSpacing: '-0.21px',
                } }),
            h5: Object.assign(Object.assign({ fontSize: '32px', lineHeight: '40px', letterSpacing: '-0.2px', fontWeight: 700 }, (0, fonts_1.supportVariableFont)('bold', { fontVariationSettings: "'wdth' 700" })), { '@media (max-width:600px)': {
                    fontSize: '20px',
                    lineHeight: '24px',
                } }),
            h6: Object.assign(Object.assign({ fontSize: '24px', lineHeight: '28px', letterSpacing: '-0.2px', fontWeight: 700 }, (0, fonts_1.supportVariableFont)('bold', { fontVariationSettings: "'wdth' 700" })), { '@media (max-width:600px)': {
                    fontSize: '20px',
                    lineHeight: '24px',
                } }),
            subtitle1: Object.assign(Object.assign({ fontSize: '20px', lineHeight: '24px', letterSpacing: '-0.5px', fontWeight: 700 }, (0, fonts_1.supportVariableFont)('bold', { fontVariationSettings: "'wdth' 700" })), { '@media (max-width:600px)': {
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '-0.4px',
                } }),
            subtitle2: Object.assign(Object.assign({ fontSize: '20px', lineHeight: '24px', letterSpacing: '-0.5px', fontWeight: 400 }, (0, fonts_1.supportVariableFont)('regular', {
                fontVariationSettings: "'wdth' 400",
            })), { '@media (max-width:600px)': {
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '-0.4px',
                } }),
            body1: Object.assign({ fontSize: '16px', lineHeight: '20px', letterSpacing: '-0.4px', fontWeight: 400 }, (0, fonts_1.supportVariableFont)('regular', {
                fontVariationSettings: "'wdth' 400",
            })),
            body2: Object.assign({ fontSize: '14px', lineHeight: '16px', letterSpacing: '-0.2px', fontWeight: 400 }, (0, fonts_1.supportVariableFont)('regular', {
                fontVariationSettings: "'wdth' 400",
            })),
            button: Object.assign({ textTransform: 'none', fontWeight: 400 }, (0, fonts_1.supportVariableFont)('regular', {
                fontVariationSettings: "'wdth' 400",
            })),
        },
    };
    return (0, styles_1.createTheme)(themeOptions);
}
exports.createWebsiteTheme = createWebsiteTheme;
//# sourceMappingURL=project-citrus.js.map